import {type ReleaseState} from '@sanity/client'
import {Card} from '@sanity/ui'
import {type ForwardedRef, forwardRef, useMemo} from 'react'
import {IntentLink} from 'sanity/router'

import {type PreviewLayoutKey} from '../../../components/previews/types'
import {DocumentPreviewPresence} from '../../../presence'
import {SanityDefaultPreview} from '../../../preview/components/SanityDefaultPreview'
import {useDocumentPresence} from '../../../store/presence/useDocumentPresence'
import {useDocumentPreviewValues} from '../../../tasks/hooks/useDocumentPreviewValues'
import {getPublishedId} from '../../../util/draftUtils'
import {getReleaseIdFromReleaseDocumentId} from '../../util/getReleaseIdFromReleaseDocumentId'

interface ReleaseDocumentPreviewProps {
  documentId: string
  documentTypeName: string
  releaseId: string
  releaseState?: ReleaseState
  documentRevision?: string
  hasValidationError?: boolean
  layout?: PreviewLayoutKey
  isGoingToBePublished?: boolean
  isCardinalityOneRelease?: boolean
}

const isArchivedRelease = (releaseState: ReleaseState | undefined) =>
  releaseState === 'archived' || releaseState === 'archiving' || releaseState === 'unarchiving'

export function ReleaseDocumentPreview({
  documentId,
  documentTypeName,
  releaseId,
  releaseState,
  isCardinalityOneRelease,
  documentRevision,
  layout,
  isGoingToBePublished = false,
}: ReleaseDocumentPreviewProps) {
  const documentPresence = useDocumentPresence(documentId)

  const intentParams = useMemo(() => {
    if (isCardinalityOneRelease) {
      return {
        scheduledDraft: getReleaseIdFromReleaseDocumentId(releaseId),
      }
    }
    if (releaseState === 'published') {
      // We are inspecting this document through the published view of the doc.
      return {
        rev: `@release:${getReleaseIdFromReleaseDocumentId(releaseId)}`,
        inspect: 'sanity/structure/history',
      }
    }

    if (releaseState === 'archived') {
      // We are "faking" the release as if it is still valid only to render the document
      return {
        rev: '@lastEdited',
        inspect: 'sanity/structure/history',
        historyEvent: documentRevision,
        historyVersion: getReleaseIdFromReleaseDocumentId(releaseId),
        archivedRelease: 'true',
      }
    }

    return {}
  }, [releaseState, releaseId, documentRevision, isCardinalityOneRelease])

  const LinkComponent = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-shadow
      forwardRef(function LinkComponent(linkProps, ref: ForwardedRef<HTMLAnchorElement>) {
        return (
          <IntentLink
            {...linkProps}
            intent="edit"
            params={{
              id: getPublishedId(documentId),
              type: documentTypeName,
              ...intentParams,
            }}
            searchParams={
              isCardinalityOneRelease || isArchivedRelease(releaseState)
                ? undefined
                : [
                    [
                      'perspective',
                      releaseState === 'published'
                        ? 'published'
                        : getReleaseIdFromReleaseDocumentId(releaseId),
                    ],
                  ]
            }
            ref={ref}
          />
        )
      }),
    [documentId, documentTypeName, intentParams, releaseState, releaseId, isCardinalityOneRelease],
  )

  const previewPresence = useMemo(
    () => documentPresence?.length > 0 && <DocumentPreviewPresence presence={documentPresence} />,
    [documentPresence],
  )

  const {isLoading: previewLoading, value: resolvedPreview} = useDocumentPreviewValues({
    documentId: isGoingToBePublished ? getPublishedId(documentId) : documentId,
    documentType: documentTypeName,
    perspectiveStack: isGoingToBePublished ? [] : [getReleaseIdFromReleaseDocumentId(releaseId)],
  })

  return (
    <Card tone="inherit" as={LinkComponent} radius={2} data-as="a">
      <SanityDefaultPreview
        {...(resolvedPreview || {})}
        status={previewPresence}
        isPlaceholder={previewLoading}
        layout={layout}
      />
    </Card>
  )
}
